import { operators as o, common as c } from '@pendo/aggregations';
import { defaultTimeSeriesWithRetentionDays } from '@/session-replay/helpers/common.util';
import { convertTimeSeriesForAggregation } from '@/utils/date-range.utils';
import isString from 'lodash/isString';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';

type ReplayRecordingParams = {
    appId?: c.AppIdType;
    startTime?: number;
    endTime?: number;
    recordingSessionId: string;
    crossTabSessions?: string[];
    visitorId?: string;
    canUsePlaybackPerf?: boolean;
    replayRetentionDays?: number;
};

export default {
    name: 'ReplayRecordingAggregation',
    responseFormat: 'rows',
    productArea: PRODUCT_AREAS.SESSION_REPLAY,
    subProductArea: SUB_PRODUCT_AREAS.PLAYER,
    build: ({
        appId = 'expandAppIds("*")',
        startTime,
        endTime,
        visitorId,
        recordingSessionId,
        crossTabSessions = [],
        canUsePlaybackPerf = false,
        replayRetentionDays,
    }: ReplayRecordingParams) => {
        let timeSeries;
        if (startTime && endTime) {
            timeSeries = {
                period: 'dayRange',
                first: `${startTime}`,
                last: `${endTime}`,
            };
        } else {
            timeSeries = convertTimeSeriesForAggregation(defaultTimeSeriesWithRetentionDays(replayRetentionDays));
        }

        const hasCrossTabSessions = Boolean(crossTabSessions.length);
        const formattedRecordingSessionIdList = hasCrossTabSessions
            ? crossTabSessions.map((recordingSessionId) => JSON.stringify(recordingSessionId)).join(',')
            : [`"${recordingSessionId}"`];

        return o.pipeline(
            o.sources.recordingMetadata({
                appId,
                timeSeries,
                blacklist: 'ignore',
            }),
            o.filter('!isBroken'),
            isString(visitorId) && o.filter(`visitorId == "${visitorId}"`),
            o.filter(`contains([${formattedRecordingSessionIdList}], recordingSessionId)`),
            // recordingRrwebEventCount and recordingHasKeyFrame filter out chunks that are just a Meta + snapshot
            // without a keyframe, which causes the player to hang. You should almost NEVER need to use
            // recordingRrwebEventCount as it has serious performance implications. rrwebEventCount is a different
            // field/value, but it is what you should use 99.9% of the time. recordingRrwebEventCount is OK to use
            // here only b/c this agg runs over such a small amount of data (usually only a few recordingSessionIds).
            o.filter('isSessionStart || recordingRrwebEventCount > 2'),
            o.filter('recordingHasKeyFrame'),
            o.fork(
                o.pipeline(
                    o.group(
                        ['visitorId'],
                        o.groupField('appId', { first: 'appId' }),
                        o.groupField('accountId', { first: 'accountId' }),
                        o.groupField('visitorId', { first: 'visitorId' }),
                        o.groupField('tabId', { first: 'tabId' }),
                        o.groupField('startTime', { min: 'startTime' }),
                        o.groupField('endTime', { max: 'endTime' }),
                        o.groupField('minBrowserTime', { min: 'minBrowserTime' }),
                        o.groupField('maxBrowserTime', { max: 'maxBrowserTime' }),
                        o.groupField('recordingIds', { list: 'recordingId' }),
                        canUsePlaybackPerf && !hasCrossTabSessions
                            ? o.groupField('inactivityPeriods', o.inactivityPeriods())
                            : undefined,
                        canUsePlaybackPerf ? o.groupField('recordingSize', { sum: 'recordingSize' }) : undefined,
                    ),
                ),
                o.pipeline(
                    o.group(
                        ['recordingId', 'recordingSessionId'],
                        o.groupField('chunkSize', { sum: 'recordingSize' }),
                        o.groupField('chunkStartTime', { min: 'recordingStartTime' }),
                        o.groupField('chunkEndTime', { max: 'recordingEndTime' }),
                    ),
                    o.sort('chunkStartTime'),
                ),
                o.pipeline(
                    o.group(
                        ['visitorId', 'accountId', 'recordingSessionId'],
                        o.groupField('activityTimelineTimestamps', { concat: 'activityTimelineTimestamps' }),
                        hasCrossTabSessions ? o.groupField('startTime', { min: 'startTime' }) : undefined,
                        hasCrossTabSessions ? o.groupField('endTime', { max: 'endTime' }) : undefined,
                        hasCrossTabSessions ? o.groupField('appId', { first: 'appId' }) : undefined,
                        hasCrossTabSessions ? o.groupField('minBrowserTime', { min: 'minBrowserTime' }) : undefined,
                        hasCrossTabSessions ? o.groupField('maxBrowserTime', { max: 'maxBrowserTime' }) : undefined,
                        canUsePlaybackPerf && hasCrossTabSessions
                            ? o.groupField('inactivityPeriods', o.inactivityPeriods())
                            : undefined,
                    ),
                    o.evalExpression({ activityTimelineTimestamps: 'sortUnique(activityTimelineTimestamps)' }),
                ),
            ),
        );
    },
};
